/**
 * Shared types and layout constants for the New Entry redesign.
 * Lives here (not on the route page) so presentational components can import them
 * without depending on `EventNewEntryPage`.
 */

/** Element ids for `scrollIntoView` from Entry Summary edit actions (keep in sync with the page handler). */
export const NEW_ENTRY_SCROLL = {
    header: "rsp-new-entry-header",
    stabling: "rsp-new-entry-stabling",
    waivers: "rsp-new-entry-waivers",
    summary: "rsp-new-entry-summary",
    entry: (oneBasedIndex: number) => `rsp-new-entry-entry-${oneBasedIndex}`,
    entryNew: "rsp-new-entry-entry-new",
} as const;

/** Which parent section (or numbered entry card) is currently expanded. `undefined` means all collapsed. */
export type ExpandedCardStates = "header" | "entry" | `entry${number}` | "stabling" | "waivers" | "summary" | "review" | undefined;

/** Collapsed grid includes one tile each for “Add New Entry” and “Copy Previous Entry”. */
export const ADD_ACTION_TILE_COUNT = 2;

/** Max collapsed entry cards per row. */
export const ENTRY_GRID_MAX_COLUMNS = 3;
/** A column is only allowed if the grid can spare at least this many px per card. */
export const ENTRY_CARD_MIN_WIDTH_PX = 280;
/** Horizontal chrome outside the horse-title text (card margins, header pad, emoji, gap). */
export const ENTRY_TITLE_CHROME_PX = 120;
/** Vertical gap between compact grid rows and stacked parent sections (matches `.mt-2`). */
export const ENTRY_SECTION_GAP_PX = 8;
/** Horizontal padding on section wrappers — pairs with card `mx-1` so edges line up. */
export const SECTION_HORIZONTAL_PADDING_PX = 6;
/** Matches `mx-1` (0.25rem). Adjacent card margins form the 8px column gutter. */
export const COMPACT_CARD_MARGIN_X_PX = 4;

/** True when a horse/entry card is open (`"entry"` draft or `"entry1"`, `"entry2"`, …). */
export function isAnyEntryExpanded(expandedCard: ExpandedCardStates): boolean {
    return expandedCard === "entry" || (typeof expandedCard === "string" && /^entry\d+$/.test(expandedCard));
}
